<template>
  <overlay
    v-for="(block) of videoBlocks"
    :key="`video-player-overlay-${block.id}`"
    block-class="video-player-overlay"
    direction="right"
    :namespace="`video-player-overlay-${block.id}`"
    template
    type="modal"
    teleport
    :show-overlay="false"
    icon-cross
  >
    <template #body="{ isActive }">
      <video-player
        v-if="
          block.videoControls?.videoModalEnable && block.mobileVideo ||
            block.videoControls?.videoModalEnable && block.desktopVideo
        "
        video-modal
        :is-active="isActive"
        :video="isMobile ? block.mobileVideo : block.desktopVideo"
        :video-controls="block.videoControls"
        :class="[
          !block.mobileVideo && 'mobile-hidden',
          !block.desktopVideo && 'desktop-hidden',
        ]"
        :show-overlay="
          isMobile && block.mobileVideo || !isMobile && block.desktopVideo
        "
        :image="isMobile ? block.mobileImage : block.desktopImage"
        @video-loaded="$emit('carousel-image-loaded', true)"
      />
    </template>
  </overlay>

  <div
    ref="carouselWrapper"
    class="media-carousel__container"
  >
    <div
      v-if="blocks.length > 1"
      ref="carousel"
      class="media-carousel__carousel splide"
      :js-carousel="`${section.id}`"
    >
      <div class="media-carousel__track splide__track">
        <ul class="media-carousel__slides splide__list">
          <li
            v-for="(block, index) of blocks"
            :key="`splide-${block.id}-${index}`"
            class="media-carousel__slide splide__slide"
            :class="[
              block.styles,
              section.layout === 'narrow' ? null : 'fixedHeight',
            ]"
          >
            <carousel-slide
              :block="block"
              :layout="section.layout"
              @carousel-image-loaded="imagesLoaded = true"
            />
          </li>
        </ul>
      </div>

      <div class="media-carousel__arrows splide__arrows">
        <transition name="fade">
          <button
            v-show="showPrevArrow"
            class="splide__arrow splide__arrow--prev splide__arrow--transition"
            type="button"
            :aria-label="$string('accessibility.carousel.previous')"
          >
            <icon-left />
          </button>
        </transition>

        <transition name="fade">
          <button
            v-show="showNextArrow"
            class="splide__arrow splide__arrow--next splide__arrow--transition"
            type="button"
            :aria-label="$string('accessibility.carousel.next')"
          >
            <icon-right />
          </button>
        </transition>
      </div>

      <ul class="media-carousel__dots splide__pagination" />
    </div>

    <div
      v-else
      ref="singleCarousel"
      class="media-carousel__single-slide"
    >
      <div
        v-for="(block, index) of blocks"
        :key="`${block.id}-${index}`"
        class="media-carousel__slide"
        :class="[
          block.styles,
          section.layout === 'narrow' ? null : 'fixedHeight',
        ]"
      >
        <carousel-slide
          :block="block"
          :layout="section.layout"
          @carousel-image-loaded="imagesLoaded = true"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Media carousel (media-carousel)
 * -----------------------------------------------------------------------------
 * A full screen element which houses a carousel and additional copy.
 *
 * @param {String} blocks.ctaText - Slide CTA text.
 * @param {String} blocks.ctaUrl - Slide CTA url.
 * @param {String} blocks.styles - Slide style customization values.
 * @param {String} blocks.subtitle - Slide subtitle text.
 * @param {String} blocks.text - Slide body text.
 * @param {String} blocks.title - Slide title text.
 *
 */
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { mapState } from 'vuex'

import Carousel from '~/api/carousel'

import CarouselSlide from '~async/media-carousel/carousel-slide'
import Overlay from '~global/overlay/overlay'
import VideoPlayer from '~global/video-player/video-player'

import IconLeft from '~icons/direction/arrow/left.svg'
import IconRight from '~icons/direction/arrow/right.svg'

gsap.registerPlugin(ScrollTrigger)

ScrollTrigger.config({
  limitCallbacks: true,
  ignoreMobileResize: true,
})

export default {
  name: 'MediaCarousel',

  components: {
    CarouselSlide,
    IconLeft,
    IconRight,
    Overlay,
    VideoPlayer,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [
        {
          ctaText: '',
          ctaUrl: '',
          desktopImage: '',
          mobileImage: '',
          styles: '',
          subtitle: '',
          text: '',
          title: '',
        },
      ],
    },
    section: {
      type: Object,
      default: () => ({
        id: '',
        animate: false,
        layout: '',
      }),
    },
  },

  emits: {
    'carousel-image-loaded': () => true,
  },

  data() {
    return {
      slide: {
        carousel: false,
        config: {
          arrows: true,
          autoHeight: true,
          classes: {
            pagination: 'splide__pagination',
          },
          drag: true,
          dragMinThreshold: {
            mouse: 0,
            touch: 40,
          },
          easing: 'ease-in-out',
          pagination: true,
          snap: true,
          speed: 400,
          type: 'slide',
          updateOnMove: true,
          waitForTransition: false,
          breakpoints: {
            1024: {
              speed: 1500,
            },
          },
        },
      },
      current: {
        slideIndex: 0,
        copy: '',
      },
      imagesLoaded: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Calculate whether to show previous arrow button.
     * - Don't show if on first slide
     * @returns {Boolean}
     */
    showPrevArrow() {
      return this.current.slideIndex !== 0
    },

    /**
     * Calculate whether to show next arrow button.
     * - Don't show if on last slide
     * @returns {Boolean}
     */
    showNextArrow() {
      return this.current.slideIndex !== (this.blocks.length - 1)
    },

    /**
     * Return blocks with video modal enable.
     * @returns {Array}
     */
    videoBlocks() {
      return this.blocks.filter((block) => {
        return block.videoControls?.videoModalEnable
      })
    },
  },

  watch: {

    /**
     * Hacky but we only want to create the carousel when the first image is
     * loaded and available to render. Prevents a flash when the carousel loads
     * over the top of the liquid layer.
     */
    imagesLoaded: {
      handler(value) {
        if (!value) {
          return
        }

        if (this.blocks.length === 1) {
          this.initScrollTrigger()
          return
        }

        if (this.slide.carousel) {
          return
        }
        this.initCarousel()
      },
    },
  },

  mounted() {
    setTimeout(() => {
      this.removePlaceholders()

      if (!this.slide?.carousel) {
        this.initCarousel()
      }
    }, this.$timing('slow'))
  },

  methods: {

    /**
     * Initalize Carousel
     */
    initCarousel() {
      if (!this.$refs.carousel) {
        return
      }

      const carousel = Carousel(`[js-carousel="${this.section.id}"]`, {
        ...this.slide.config,
      })

      if (!carousel) {
        return
      }

      carousel.on('mounted', () => {
        this.initScrollTrigger()

        setTimeout(() => {
          this.removePlaceholders()
        }, this.$timing('quick'))
      })

      carousel.mount()
      this.slide.carousel = carousel

      /**
       * Carousel slide has just updated.
       */
      this.slide.carousel.on('move', () => {
        this.current.slideIndex = Number(this.slide.carousel.index)
      })
    },

    /**
     * Removes liquid placeholder content when media content loads in the
     * Vue component overlaying it.
     */
    removePlaceholders() {
      const parent = this.$refs.carouselWrapper?.closest('.liquid-removed-parent')
      if (!parent) {
        return
      }
      const placeholders = [...parent.querySelectorAll('.liquid-removed')]
      if (!placeholders.length) {
        return
      }
      placeholders.forEach(placeholder => {
        placeholder.remove()
      })
    },

    /**
     * Initialise scroll-trigger.
     * - Creates a gsap timeline linked to scroll.
     *   - {scrub} tells the animation to be linked to scroll
     *   - {markers} - set to `true` to get some markers to help with debugging.
     */
    initScrollTrigger() {
      if (!this.section.animate) {
        return
      }

      if (this.blocks.length > 1) {
        gsap.from(this.$refs.carousel.querySelector('.media-carousel__media'), {
          scrollTrigger: {
            trigger: this.$refs.carousel,
            scrub: true,
            start: 'top bottom-=10%',
            end: 'top 40%',
          },
          clipPath: 'inset(5% round 45px)',
        })

        gsap.from(this.$refs.carousel.querySelector('.media-carousel__content'), {
          scrollTrigger: {
            trigger: this.$refs.carousel,
            scrub: true,
            start: 'top bottom-=10%',
            end: 'top 30%',
          },
          y: 75,
        })

        return
      }

      gsap.from(this.$refs.singleCarousel.querySelector('.media-carousel__media'), {
        scrollTrigger: {
          trigger: this.$refs.singleCarousel,
          scrub: true,
          start: 'top bottom-=10%',
          end: 'top 40%',
        },
        clipPath: 'inset(5% round 45px)',
      })

      gsap.from(this.$refs.singleCarousel.querySelector('.media-carousel__content'), {
        scrollTrigger: {
          trigger: this.$refs.singleCarousel,
          scrub: true,
          start: 'top bottom-=10%',
          end: 'top 30%',
        },
        y: 75,
      })
    },
  },
}
</script>

<style lang="scss">
@import './media-carousel';
</style>
